
import { supabase } from '@/integrations/supabase/client';

export const generateDeviceCode = (): string => {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';

  const randomLetters = Array.from({ length: 4 }, () =>
    letters.charAt(Math.floor(Math.random() * letters.length))
  ).join('');

  const randomNumbers = Array.from({ length: 4 }, () =>
    numbers.charAt(Math.floor(Math.random() * numbers.length))
  ).join('');

  // Exemplo de saída: "GMAZ-0224"
  return `${randomLetters}-${randomNumbers}`;
};

/**
 * Formata um código de dispositivo para o padrão XXXX-XXXX
 * Se o código já tiver um hífen, retorna como está
 * Se não tiver, adiciona um hífen após os primeiros 4 caracteres
 */
export const formatDeviceCode = (code: string): string => {
  if (!code) return '';

  // Remover qualquer hífen existente e transformar em maiúsculas
  const cleanCode = code.replace(/-/g, '').toUpperCase();

  // Se tiver 8 caracteres, formatar como XXXX-XXXX
  if (cleanCode.length === 8) {
    return `${cleanCode.slice(0, 4)}-${cleanCode.slice(4)}`;
  }

  // Caso contrário, retorna o código original em maiúsculas
  return cleanCode;
};

/**
 * Gera um device ID único para identificar o dispositivo
 */
export const getDeviceId = (): string => {
  const stored = localStorage.getItem('player_device_id');
  if (stored) return stored;

  const newId = `player_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  localStorage.setItem('player_device_id', newId);
  return newId;
};

/**
 * Sincroniza o código do dispositivo com a tabela device_pairings
 * Isso permite que o Companion ative este dispositivo
 */
export const syncCodeWithPairings = async (code: string): Promise<boolean> => {
  try {
    const cleanCode = code.replace(/-/g, '').toUpperCase();
    const deviceId = getDeviceId();

    console.log('[DeviceCode] Sincronizando código com device_pairings:', cleanCode);

    // Verificar se já existe pareamento com este device_id
    const { data: existing } = await supabase
      .from('device_pairings')
      .select('id, pairing_code, status')
      .eq('device_id', deviceId)
      .limit(1)
      .maybeSingle();

    if (existing) {
      // Se já existe e o código é diferente, atualizar
      if (existing.pairing_code !== cleanCode) {
        console.log('[DeviceCode] Atualizando código existente:', existing.pairing_code, '->', cleanCode);
        await supabase
          .from('device_pairings')
          .update({
            pairing_code: cleanCode,
            status: 'pending',
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);
      } else {
        console.log('[DeviceCode] Código já existe na tabela:', cleanCode);
      }
      return true;
    }

    // Se não existe, inserir novo
    console.log('[DeviceCode] Inserindo novo pareamento:', cleanCode);
    const { error } = await supabase
      .from('device_pairings')
      .insert({
        pairing_code: cleanCode,
        device_id: deviceId,
        status: 'pending'
      });

    if (error) {
      // Se erro for de código duplicado, tentar atualizar
      if (error.code === '23505') {
        console.log('[DeviceCode] Código já existe, tentando atualizar device_id...');
        await supabase
          .from('device_pairings')
          .update({
            device_id: deviceId,
            status: 'pending',
            updated_at: new Date().toISOString()
          })
          .eq('pairing_code', cleanCode);
      } else {
        console.error('[DeviceCode] Erro ao inserir pareamento:', error);
        return false;
      }
    }

    console.log('[DeviceCode] ✅ Código sincronizado com sucesso!');
    return true;

  } catch (error) {
    console.error('[DeviceCode] Erro ao sincronizar código:', error);
    return false;
  }
};

/**
 * Verifica se o código foi ativado pelo Companion
 * Retorna o driver_id se ativo, null se ainda pendente
 */
export const checkPairingActivation = async (code: string): Promise<{ isActive: boolean; driverId: string | null }> => {
  try {
    const cleanCode = code.replace(/-/g, '').toUpperCase();

    const { data, error } = await supabase
      .from('device_pairings')
      .select('status, driver_id')
      .eq('pairing_code', cleanCode)
      .limit(1)
      .maybeSingle();

    if (error || !data) {
      return { isActive: false, driverId: null };
    }

    if (data.status === 'active' && data.driver_id) {
      console.log('[DeviceCode] ✅ Pareamento ativo! Driver:', data.driver_id);
      return { isActive: true, driverId: data.driver_id };
    }

    return { isActive: false, driverId: null };

  } catch (error) {
    console.error('[DeviceCode] Erro ao verificar ativação:', error);
    return { isActive: false, driverId: null };
  }
};
